<template>
  <div>
    <request-form :can-back="true" @submit.prevent="doSubmit" @back="toTop">
      <date-browse-item label="依頼日" :value="requestAt" />
      <div v-if="this.infoAcquired">
        <div v-if="this.buildingSearchType === 0">
          <vuex-building-form-item bind="building_id" :options="buildings" :required="true" @change="doChangeBuilding" />
        </div>
        <div v-else-if="this.buildingSearchType === 1 || this.buildingSearchType === 2">
          <vuex-building-text-form-item bind="building_id"
                                        :options="buildings"
                                        :required="true"
                                        :placeholder="isBuildingSearchWordCount + '文字以上入力してください'"
                                        :minlength="isBuildingSearchWordCount"
                                        :searchType="buildingSearchType"
                                        @select="doChangeBuilding"
          />
        </div>
      </div>
      <vuex-text-form-item label="依頼物件住戸番号" bind="building_house_number" :required="true" />
      <vuex-text-form-item label="所有者" bind="owner_name" :required="true" />
      <info-alert-browse-item v-if="ownerNameExplanation" label="" :message="ownerNameExplanation" />
      <div v-show="requiredDocumentOption !== $constant.REQUIRED_DOCUMENT_OPTIONS.HIDDEN">
        <vuex-document-upload-form-item :required="!isOwner" :isOwner="isOwner" :required-document-option="requiredDocumentOption" />
        <info-alert-browse-item v-if="requiredDocumentExplanation" label="" :message="requiredDocumentExplanation" />
      </div>
      <vuex-issue-document-form-item bind="issue_documents" :issue-documents="issueDocuments" :required="true" />
      <info-alert-browse-item v-if="issueDocumentExplanation" label="" :message="issueDocumentExplanation" />
      <text-browse-item label="手数料の支払方法" :value="paymentMethodName" />
      <vuex-textarea-form-item label="備考" bind="note" />
      <vuex-text-with-button-form-item v-if="isOwner" label="依頼者住所" bind="owner_address" :required="true">
        <template #append>
          <button type="button" class="btn btn-outline-secondary" @click="copyToCompanyAddress">物件住所取得</button>
        </template>
      </vuex-text-with-button-form-item>
      <text-browse-item v-else label="会社住所" :value="companyAddress" />
      <text-browse-item v-if="!isOwner" label="会社名" :value="company.name" />
      <text-browse-item v-if="!isOwner" label="会社住所" :value="companyAddress" />
      <vuex-textarea-form-item label="連絡先" bind="contact" :required="true" />
      <licence-browse-item v-if="!isOwner" label="免許番号" :type="company.licence_type" :number="company.licence_number" />
      <info-alert v-if="wholeExplanation" :message="wholeExplanation" />
      <danger-alert v-if="alertMessage" :message="alertMessage" />
      <template #expansion-buttons-right>
        <change-status-button text="キャンセル" class="btn-danger px-4"
                              @click="confirmOperateWithReason($constant.ISSUANCE_REQUEST_OPERATION.CANCELED)"
        />
      </template>
    </request-form>
    <teleport to="body">
      <edit-confirm-modal-with-input-area first-description-text="理由を入力して更新ボタンを押下してください。"
                                          second-description-text="入力した内容は管理会社に通知されます。"
                                          input-text-label="理由"
                                          :text-max-length="5000"
                                          :required="true"
                                          @beforeUpdate="setNotificationReason"
                                          @update="doOperation()"
                                          ref="confirmModalWithReason"
                                          default-value="不要になったため。"
      />
    </teleport>
  </div>
</template>

<script>
import {mapState, mapMutations} from 'vuex';
import {convertPrefectureCodeToName} from '@/constants/prefecture';
import {convertPaymentMethodToName} from '@/constants/payment_method';
import AddonLoadPageEvent from '@/mixin/AddonLoadPageEvent';
import RequestForm from "@/views/templates/forms/RequestForm";
import DateBrowseItem from "@/views/molecules/browseItems/DateBrowseItem";
import VuexBuildingFormItem from '@/views/molecules/formItems/VuexBuildingFormItem';
import VuexBuildingTextFormItem from '@/views/molecules/formItems/VuexBuildingTextFormItem';
import VuexTextFormItem from "@/views/molecules/formItems/VuexTextFormItem";
import VuexDocumentUploadFormItem from "@/views/molecules/formItems/VuexDocumentUploadFormItem";
import VuexIssueDocumentFormItem from '@/views/molecules/formItems/VuexIssueDocumentFormItem';
import TextBrowseItem from "@/views/molecules/browseItems/TextBrowseItem";
import VuexTextareaFormItem from "@/views/molecules/formItems/VuexTextareaFormItem";
import LicenceBrowseItem from "@/views/molecules/browseItems/LicenceBrowseItem";
import ChangeStatusButton from '@/views/molecules/buttons/ChangeStatusButton';
import EditConfirmModalWithInputArea from "@/views/molecules/modals/EditConfirmModalWithInputArea";
import DangerAlert from "@/views/atoms/alerts/DangerAlert";
import InfoAlertBrowseItem from "@/views/molecules/browseItems/InfoAlertBrowseItem";
import InfoAlert from "@/views/atoms/alerts/InfoAlert";
import VuexTextWithButtonFormItem from "@/views/molecules/formItems/VuexTextWithButtonFormItem";

export default {
  name: "IssuanceRequestEditor",
  components: {
    RequestForm,
    DateBrowseItem,
    VuexBuildingFormItem,
    VuexBuildingTextFormItem,
    VuexTextFormItem,
    VuexDocumentUploadFormItem,
    VuexIssueDocumentFormItem,
    TextBrowseItem,
    VuexTextareaFormItem,
    LicenceBrowseItem,
    ChangeStatusButton,
    EditConfirmModalWithInputArea,
    DangerAlert,
    InfoAlertBrowseItem,
    InfoAlert,
    VuexTextWithButtonFormItem,
  },
  inject: [
    '$constant',
  ],
  mixins: [AddonLoadPageEvent],
  provide: {
    bindModule: 'issuance_request_form'
  },
  data() {
    return {
      operation: null,
      alertMessage: null,
    }
  },
  computed: {
    ...mapState('issuance_request_form', {
      buildings: state => state.buildings,
      issueDocuments: state => state.issueDocuments,
      requestAt: state => state.data.request_at,
      payment_method_code: state => state.data.payment_method,
      status: state => state.data.status,
      contact: state => state.data.contact,
      ownerNameExplanation: state => state.data.owner_name_explanation,
      requiredDocumentExplanation: state => state.data.required_document_explanation,
      issueDocumentExplanation: state => state.data.issue_document_explanation,
      wholeExplanation: state => state.data.whole_explanation,
      managementCompanyCode: state => state.data.building.management_company.code,
      buildingSearchType: state => state.data.building.management_company.building_search_type,
      buildingSearchWordCount: state => state.data.building.management_company.building_search_word_count,
      infoAcquired: state => state.data.building.management_company.building_search_type !== null,
      requiredDocumentOption: state => state.data.building.management_company.required_document_option,
      ownerAddress: state => state.data.owner_address,
    }),
    ...mapState('account', {
      isGuest: state => state.is_guest,
      company: state => state.company,
      guestIssuanceRequestId: state => state.guest.issuance_request_id,
      guestType: state => state.guest_type,
    }),
    companyAddress() {
      return convertPrefectureCodeToName(this.company.prefecture_code) + this.company.address1 + (this.company.address2 ?? '');
    },
    confirmRouteName() {
      return this.isGuest ? 'GuestIssuanceRequestEditorConfirm' : 'IssuanceRequestEditorConfirm';
    },
    issuanceRequestId() {
      return this.isGuest ? this.guestIssuanceRequestId : Number(this.$route.params.id);
    },
    paymentMethodName() {
      return convertPaymentMethodToName(this.payment_method_code);
    },
    isOwner() {
      return this.guestType === this.$constant.GUEST_TYPE.OWNER.code;
    },
    isBuildingSearchWordCount() {
      if (this.buildingSearchWordCount >= 1) {
        return this.buildingSearchWordCount;
      } else {
        return 0;
      }
    },
  },
  beforeRouteLeave(to) {
    if (!to.name.match(/^(Guest)?IssuanceRequestEditorConfirm$/)) {
      // 遷移先が想定された場所以外なら入力内容をリセットする
      this.resetData();
    }
  },
  methods: {
    ...mapMutations('issuance_request_form', [
      'resetData',
      'setIsOwner',
    ]),
    async loadPage() {
      if (this.$store.state.issuance_request_form.data.id !== this.issuanceRequestId) {
        if (this.isGuest){
          await this.$store.dispatch('account/authGuest', {
            access_key: sessionStorage.getItem('accessKey')
          }).catch((reason) => this.$root.failApi(reason));
        }
        // Vue コンポーネントの isOwner をストアに設定
        if (this.isOwner){
          this.setIsOwner(this.isOwner);
        }
        // モジュール内にデータがなければ取得（確認画面から戻ってきたときはデータが入っているので再読み込みしない）
        return this.$store.dispatch('issuance_request_form/load', {
          id: this.issuanceRequestId,
          edit: true,
          is_guest: this.isGuest,
        }).then(() => {
          if (this.buildingSearchType === 1 || this.buildingSearchType === 2) {
            const payload = {
              management_company_code: this.managementCompanyCode,
              name: null,
              name_kana: null,
              prefecture_code: null,
              address1: null,
              is_guest: this.isGuest
            };
            this.$store.dispatch('issuance_request_form/loadBuildings', payload).catch(reason => {
              if (reason?.notFound) {
                this.setWarning({key: 'building_id', value: ['依頼できる物件が存在しません。']});
              } else {
                this.$root.failApi(reason);
              }
            });
          }
        }).catch(reason => {
          if (reason?.conflict) {
            return this.$router.replace({name: 'BrokerErrorNotFound'});
          } else {
            this.$root.failApi(reason);
          }
        });
      }
    },
    async doChangeBuilding(buildingId) {
      if (this.isGuest){
        await this.$store.dispatch('account/authGuest', {
          access_key: sessionStorage.getItem('accessKey')
        }).catch((reason) => this.$root.failApi(reason));
      }
      if (buildingId) {
        const payload = {
          building_id: buildingId,
          is_guest: this.isGuest,
        }
        this.$store.dispatch('issuance_request_form/loadIssueDocuments', payload).catch((reason) => this.$root.failApi(reason));
      } else {
        this.$store.commit('issuance_request_form/setIssueDocuments', []);
      }
    },
    async doSubmit() {
      if (this.isGuest){
        await this.$store.dispatch('account/authGuest', {
          access_key: sessionStorage.getItem('accessKey')
        }).catch((reason) => this.$root.failApi(reason));
      }
      this.$store.dispatch('issuance_request_form/validate').then(() => {
        this.$router.push({name: this.confirmRouteName});
      }).catch(() => {});
    },
    toTop() {
      this.$router.push({name: 'Top'});
    },
    setNotificationReason(value) {
      this.notificationReason = value;
    },
    confirmOperateWithReason(operation) {
      this.operation = operation;
      this.$refs.confirmModalWithReason.show();
    },
    async doOperation() {
      if (this.isGuest){
        await this.$store.dispatch('account/authGuest', {
          access_key: sessionStorage.getItem('accessKey')
        }).catch((reason) => this.$root.failApi(reason));
      }
      this.$store.dispatch('issuance_request_form/operate', {
        operation: this.operation,
        notificationReason: this.notificationReason
      }).then(() => {
        this.$router.push({name: this.isGuest ? 'GuestIssuanceRequestViewer' : 'IssuanceRequestViewer', params: {'id': this.issuanceRequestId}});
        this.$root.showFlashMessage();
      }).catch((message) => {
        if (message) {
          this.alertMessage = message;
        } else {
          this.$root.failApi();
        }
      });
    },  
    async copyToCompanyAddress() {
      const buildingId = this.$store.state.issuance_request_form.data.building_id;
      const buildingName = this.$store.state.issuance_request_form.data.building.name;
      const houseNumber = this.$store.state.issuance_request_form.data.building_house_number;      
      const selectedBuilding = this.$store.state.issuance_request_form.buildings.find(building => building.id === buildingId);
      const address = selectedBuilding?.address ?? '';
      // いずれも存在しない場合は空白にして終了
      if (!buildingName && !houseNumber && !address) {
        await this.$store.commit('issuance_request_form/inputFormItem', {
          column: 'owner_address',
          value: '',
        });
        return;
      }
      // 存在する要素のみを結合
      const combinedAddress = [address, buildingName, houseNumber].filter(Boolean).join(' ');
      // 非同期処理で更新
      await this.$store.commit('issuance_request_form/inputFormItem', {
        column: 'owner_address',
        value: combinedAddress,
      });
    },
  }
}
</script>
